import { createGeneralPageEventsAndComponents } from "../technical/page-general";
import {
	expressAddClass,
	expressEventListener,
	expressQuerySelector,
	expressQuerySelectorAll,
	expressRemoveClass,
	getParentWithClassName,
	getNextSiblingWithClassName,
	hasClass,
	hide,
	show,
} from "../../common/html";

import { getCollapsibleContent, getTabContent } from "../../services/content-service";
import { createTruncate } from "../technical/truncate";
import { createSteps, IPriceDetails, IStepsComponent } from "../organisms/steps";
import { createPopup, IPopup } from "../technical/popup-new";
import { createFlipBook } from "../technical/my-namebook";
import { CreateTabs, ITabContentRequest } from "../molecules/tabs";
import {
	createCollapsable,
	ICollapsibleContentRequest,
	ICreateCollapsableDependencies,
} from "../molecules/collapsable";
import { getParameter } from "../../utils/querystring";
import { createPopupLauncher } from "../technical/popup-launcher";
import { GetProductCategoryReviews } from "../../services/reviews-service";
import { getStepContent } from "../../services/steps-service";
import { createCarrousel } from "../molecules/carrousel";
import {
	createCustomSlider,
	ICustomPhotoSliderItem,
	ICustomPhotoSliderWithDivSettings,
} from "../technical/customslider";
import { createUserReviews } from "../organisms/user-reviews";
import { initTooltip, IToolTip } from "../molecules/tooltip";
import { createPageLog } from "../molecules/page-log";
import { getPrice } from "../../services/order-service";
import { getCookie } from "../../utils/cookie";
import { createProductGrid } from "../molecules/product-grid";
import { getWishlistItems } from "../../services/wishlist-service";

window.addEventListener("DOMContentLoaded", () => {
	createGeneralPageEventsAndComponents();

	const pageLog = expressQuerySelector<HTMLElement>(document, ".technical-top-message-log", true);
	const pageLogComponent = createPageLog(pageLog);

	let stepsComponent: IStepsComponent;
	let optionPricePopups: { id: string; popup: IPopup }[];
	let priceDetails: IPriceDetails;
	let productCategory = "";
	let stepsTheme = "";
	const buttonDisabledClass = "a-button--disabled";

	const init = async () => {
		// load the wishlistItems
		window.wishlist = await getWishlistItems();

		const carrouselContainer = expressQuerySelector<HTMLElement>(document, ".m-carrousel", false);
		const productDescriptionEls = expressQuerySelectorAll<HTMLElement>(document, ".technical-product-description");
		const stepsEl = expressQuerySelector<HTMLElement>(document, "#stepsContainer");
		const tabContainerEls = expressQuerySelectorAll<HTMLElement>(document, ".technical-product-tabs");
		const createDefinitionButtons = expressQuerySelectorAll<HTMLElement>(
			document,
			".technical-createdefinition-button",
		);
		const pricePopupListEl = expressQuerySelector<HTMLElement>(document, ".technical-price-popup-list", false);
		const relatedProductsGridEl = expressQuerySelector<HTMLElement>(document, ".technical-product-grid", false);
		if (relatedProductsGridEl) {
			window.showMores = [];
			createProductGrid(relatedProductsGridEl, {});
		}
		stepsTheme = stepsEl.dataset.theme || "";
		const collapsableDeps = {
			contentSelector: ".m-collapsable__content",
			titleElSelector: ".m-collapsable__title",
			scrollIntoViewAfterOpen: false,
		} as ICreateCollapsableDependencies;

		createDefinitionButtons.forEach((createDefinitionButton) =>
			expressEventListener(createDefinitionButton, "click", () => {
				if (hasClass(createDefinitionButton, buttonDisabledClass)) return;

				// disable the button
				expressAddClass(createDefinitionButton, buttonDisabledClass);

				// hide the tooltip
				const tooltip = getNextSiblingWithClassName<HTMLElement>(createDefinitionButton, "m-tooltip");
				if (tooltip) expressAddClass(tooltip, "u-hide");

				setTimeout(() => {
					// reenable the button
					expressRemoveClass(createDefinitionButton, buttonDisabledClass);

					// reenable the tooltip
					if (tooltip) expressRemoveClass(tooltip, "u-hide");
				}, 10000); // re-enable the button after 10 sec, just in case
			}),
		);

		if (stepsEl) {
			stepsComponent = createSteps(stepsEl, {
				getStepContentAsync,
				carrouselContainer,
				priceDetails,
				pricePopupListEl,
				pageLogComponent,
			});
		} else {
			carrouselContainer && createCarrousel(carrouselContainer);
		}
		productDescriptionEls && productDescriptionEls.forEach((el) => initTruncate(el));
		loadProductCategoryReviews();
		productCategory = stepsEl.dataset.pc || "";
		optionPricePopups = [];

		const getPvcFromStepsComponent = () => {
			const ctaButtonUrl =
				stepsComponent.ctaButton.buttonEl.getAttribute("href") ??
				stepsComponent.mobileCtaButton.buttonEl.getAttribute("href");
			return (ctaButtonUrl && getParameter("pvc", ctaButtonUrl, false)) || "";
		};

		const initPriceDetails = (selectEl: HTMLSelectElement, listEls: HTMLElement[]): IPriceDetails => {
			const onSelect = () => {
				listEls.forEach((el) => hide(el));
				listEls.forEach((el) => {
					const index = el.dataset.index || -1;
					if (index === selectEl.value) show(el);
				});
			};
			const selectPvc = (pvc: string) => {
				const options = Array.from(selectEl.options);
				const optionToSelect = options.find((item) => item.dataset.pvc.toLowerCase() === pvc.toLowerCase());
				if (optionToSelect) optionToSelect.selected = true;
				onSelect();
			};
			expressEventListener(selectEl, "change", onSelect);
			return {
				selectPvc: (pvc: string) => selectPvc(pvc),
			} as IPriceDetails;
		};

		// Tabs
		if (tabContainerEls && tabContainerEls.length > 0) {
			tabContainerEls.forEach((tabContainerEl) => {
				const tabEl = expressQuerySelector<HTMLElement>(tabContainerEl, ".m-tabs", false);
				if (tabEl) {
					const initCollapsables = (tabContentEl: HTMLElement) => {
						const collapsableEls = expressQuerySelectorAll<HTMLElement>(tabContentEl, ".m-collapsable");
						if (collapsableEls && collapsableEls.length > 0)
							collapsableEls.forEach((el) => createCollapsable(el, collapsableDeps));
					};
					const onBeforeOpen = (tabContentEl: HTMLElement): Promise<void> => {
						if (!tabContentEl) return Promise.resolve();
						if (tabContentEl.innerHTML.trim() !== "") {
							initCollapsables(tabContentEl);
							return Promise.resolve();
						} else {
							if (!tabContentEl.dataset.url) return Promise.resolve();
							return getTabContent({
								id: tabContentEl.dataset.url,
								productCategory: productCategory,
								theme: stepsTheme,
							} as ITabContentRequest).then((res) => {
								tabContentEl.insertAdjacentHTML("afterbegin", res);
								initCollapsables(tabContentEl);
								// Init popup launchers
								const popupLauncherEls = expressQuerySelectorAll<HTMLElement>(
									tabContentEl,
									".technical-popup-launcher",
								);
								popupLauncherEls.forEach((el) => createPopupLauncher(el));
							});
						}
					};
					CreateTabs(tabContainerEl, { onBeforeOpen });
				}
			});
		}

		// Collapsable elements
		const collapsableContainerEl = expressQuerySelector<HTMLElement>(document, ".technical-collapsables", false);
		if (collapsableContainerEl) {
			const collapsableEls = expressQuerySelectorAll<HTMLElement>(collapsableContainerEl, ".m-collapsable");
			collapsableEls.forEach((el) => {
				const type = el.dataset.type || "";
				const contentEl = expressQuerySelector<HTMLElement>(el, ".technical-collapse-content", false);

				const getCollapseContent = (): Promise<boolean> => {
					if (contentEl.innerHTML.trim()) return Promise.resolve(true);

					return getCollapsibleContent({
						type: type,
						theme: stepsTheme,
						productCategory: productCategory,
						selectedPvc: getPvcFromStepsComponent(),
						removeCurrencySymbolFormUpperFunnelPages:
							window.context.removeCurrencySymbolFormUpperFunnelPages === "true",
						predefinedProductName: el.dataset.predefinedproductname,
					} as ICollapsibleContentRequest).then((res) => {
						if (res) {
							contentEl.insertAdjacentHTML("afterbegin", res);

							// Info links in the options collapse
							const moreInfoLinkEls = expressQuerySelectorAll<HTMLElement>(
								contentEl,
								".technical-option-price-more-info-link",
							);
							if (moreInfoLinkEls && moreInfoLinkEls.length > 0) {
								const initOptionPricesMoreInfoLink = (moreInfoLinkEl: HTMLElement) => {
									const init = (popupEl: HTMLElement) => {
										const pvcSelect = expressQuerySelector<HTMLSelectElement>(
											popupEl,
											".technical-price-option-select",
											false,
										);
										const quantityBreakListItemContainer = expressQuerySelector<HTMLElement>(
											popupEl,
											".p-option-price-details__body-container",
											true,
										);
										const quantityBreakListItems = expressQuerySelectorAll<HTMLElement>(
											quantityBreakListItemContainer,
											".m-quantity-break-list__item-container",
										);
										const noOptionsDiv = expressQuerySelector<HTMLElement>(popupEl, "#noOptionsDiv", true);
										const onChange = () => pvcSelectChange();
										const pvcSelectChange = (selectedValue?: string) => {
											let visibleItems = 0;
											if (!selectedValue) selectedValue = pvcSelect.value;
											else {
												const options = [...pvcSelect.options];
												const selectedOption = options.find(
													(o) => o.value.toLowerCase() === selectedValue.toLowerCase(),
												);
												if (selectedOption) pvcSelect.selectedIndex = selectedOption.index;
											}
											quantityBreakListItems.forEach((item) => {
												const itemPvc = item.dataset.pvc || "";
												if (itemPvc.toLowerCase() == selectedValue.toLowerCase()) {
													show(item);
													visibleItems++;
												} else {
													hide(item);
												}
											});
											if (visibleItems > 0) {
												hide(noOptionsDiv);
												show(quantityBreakListItemContainer);
											} else {
												show(noOptionsDiv);
												hide(quantityBreakListItemContainer);
											}
										};
										if (quantityBreakListItemContainer && quantityBreakListItems && noOptionsDiv) {
											hide(noOptionsDiv);
											if (!pvcSelect) return;
											const currentPvc = getPvcFromStepsComponent();
											if (currentPvc) pvcSelectChange(currentPvc);
											else pvcSelectChange();
											expressEventListener(pvcSelect, "change", onChange);
										}
									};
									const showOptionPricePopup = () => {
										const contentKey = moreInfoLinkEl.dataset.contentkey || "";

										if (contentKey) {
											let uniqueId = contentKey;
											if (uniqueId.indexOf("_") > 0) uniqueId = uniqueId.split("_")[1];
											if (optionPricePopups.filter((p) => p.id === uniqueId).length <= 0) {
												createPopup({
													dialogId: "optionpricedetails_" + uniqueId,
													view: "OptionPriceDetailsDialog",
													data: {
														productCategoryOptionContentKey: contentKey,
														pvc: getPvcFromStepsComponent(),
														showCurrency: !window.context.removeCurrencySymbolFormUpperFunnelPages,
													},
													onBeforeOpen: (popupEl) => init(popupEl),
												}).then((popup) => {
													// TODO: Do we need to save the popup? I think the popup script itself opens the existing one
													optionPricePopups.push({ id: uniqueId, popup: popup });
													popup.openAsync();
												});
											} else {
												const popup = optionPricePopups.filter((p) => p.id === uniqueId);
												if (popup[0]) popup[0].popup.openAsync();
											}
										}
									};
									const onClick = (e: Event) => {
										e.preventDefault();
										if (hasClass(moreInfoLinkEl, 'a-link--disabled"')) return;
										showOptionPricePopup();
									};
									if (moreInfoLinkEl) expressEventListener(moreInfoLinkEl, "click", onClick);
								};
								moreInfoLinkEls.forEach((el) => initOptionPricesMoreInfoLink(el));
							}

							// PriceDetails collapse
							const priceDetailsSelectEl = expressQuerySelector<HTMLSelectElement>(
								contentEl,
								".technical-price-break-select",
								false,
							);
							const priceDetailsListEls = expressQuerySelectorAll<HTMLElement>(
								contentEl,
								".technical-price-break-list",
							);
							if (priceDetailsSelectEl && priceDetailsListEls) {
								priceDetails = initPriceDetails(priceDetailsSelectEl, priceDetailsListEls);
							}

							// If the content has collapsable element => init them
							const collapsableEls = expressQuerySelectorAll<HTMLElement>(contentEl, ".m-collapsable");
							if (collapsableEls && collapsableEls.length > 0)
								collapsableEls.forEach((el) => createCollapsable(el, collapsableDeps));

							// Init popup launchers
							const popupLauncherEls = expressQuerySelectorAll<HTMLElement>(contentEl, ".technical-popup-launcher");
							popupLauncherEls.forEach((el) => createPopupLauncher(el));
						}
						return true;
					});
				};
				if (contentEl && type) {
					collapsableDeps.onBeforeOpen = getCollapseContent;
				}
				createCollapsable(el, collapsableDeps);
			});
		}

		// Product preview button?
		const productPreviews = expressQuerySelectorAll<HTMLElement>(document, ".technical-product-preview");
		productPreviews.forEach((productPreview) => {
			productPreview.addEventListener("click", () => {
				createPopup({
					data: {
						ProductCategory: productPreview.dataset["category"],
					},
					dialogId: "productPreview",
					view: "ProductPreviewDialog",
					disposeOnClose: true,
				}).then((popup) => {
					if (!popup.popup) return;
					popup.openAsync().then(() => {
						createFlipBook(expressQuerySelector(popup.popup, ".technical-flipbook-viewer"));
					});
				});
			});
		});

		// User generated content slider
		const ucgSliders = expressQuerySelectorAll<HTMLElement>(document, ".technical-slider");
		ucgSliders.forEach((x) => initGeneratedContentSlider(x));

		// Price popup usp
		if (pricePopupListEl) {
			const openPriceInfoPopupAsync = async () => {
				if (!pricePopupListEl || hasClass(pricePopupListEl, "u-hide")) return;
				return createPopup({
					view: "PriceInformation",
					dialogId: "price-info-dialog",
					forceRefresh: true,
					data: {
						productCategoryContentKey: productCategory,
						preselectedPvc: getPvcFromStepsComponent(),
					},
					onOpen: (popupEl: HTMLElement) => {
						const priceDetailsSelectEl = expressQuerySelector<HTMLSelectElement>(
							popupEl,
							".technical-price-break-select",
							false,
						);
						const priceDetailsListEls = expressQuerySelectorAll<HTMLElement>(popupEl, ".technical-price-break-list");
						if (priceDetailsSelectEl && priceDetailsListEls) {
							priceDetails = initPriceDetails(priceDetailsSelectEl, priceDetailsListEls);
						}
					},
				}).then((popup) => {
					if (!popup.popup) return;
					return popup.openAsync();
				});
			};
			const pricePopupLinkEl = expressQuerySelector(pricePopupListEl, ".technical-price-popup");
			expressEventListener(pricePopupLinkEl, "click", (e) => {
				e.preventDefault();
				openPriceInfoPopupAsync();
			});
		}
	};

	const getStepContentAsync = (
		selectedAttributeValueMa: string[],
		stepIndex: number,
		optionGroup?: string,
		targetDeliveryDate?: string,
		theme?: string,
		predefinedProductName?: string,
	) => {
		if (productCategory && selectedAttributeValueMa && stepIndex)
			return getStepContent(
				productCategory,
				selectedAttributeValueMa,
				stepIndex,
				optionGroup,
				targetDeliveryDate,
				theme ?? stepsTheme,
				predefinedProductName,
			);
		else return Promise.resolve("");
	};

	const initTruncate = (productDescriptionEl: HTMLElement) => {
		const truncateEl = expressQuerySelector<HTMLElement>(productDescriptionEl, ".technical-shave", false);
		if (!truncateEl) return;
		createTruncate(truncateEl, {
			lines: parseInt(truncateEl.dataset.lines, 10) || 0,
		});
	};

	const loadProductCategoryReviews = () => {
		const userReviewsSectionEl = expressQuerySelector<HTMLElement>(document, ".o-user-reviews", false);
		const productCategoryId = userReviewsSectionEl && userReviewsSectionEl.getAttribute("data-prod-cat-id");
		const theme = (userReviewsSectionEl && userReviewsSectionEl.getAttribute("data-theme")) || stepsTheme;
		const nameLabel = (userReviewsSectionEl && userReviewsSectionEl.getAttribute("data-namelabel")) || "";
		const anonymousLabel = (userReviewsSectionEl && userReviewsSectionEl.getAttribute("data-anonymouslabel")) || "";

		if (productCategoryId && userReviewsSectionEl) {
			const reviewsContainerEl = expressQuerySelector<HTMLElement>(userReviewsSectionEl, ".m-review-blocks", true);

			const numberOfReviewsToShow = parseInt(reviewsContainerEl.getAttribute("data-amount-review-to-show")!, 10);
			const totalAmountOfReviews = parseInt(
				expressQuerySelector<HTMLElement>(userReviewsSectionEl, ".m-review-bars__total-reviews", true).getAttribute(
					"data-total-reviews",
				)!,
				10,
			);

			const showLoadMoreButton = numberOfReviewsToShow < totalAmountOfReviews;

			const getReviewsAsync = (page: number, filter?: number, lang?: string) => {
				return GetProductCategoryReviews(productCategoryId, page, filter, lang, theme);
			};

			createUserReviews(
				userReviewsSectionEl,
				{
					getReviewsAsync,
					totalAmountOfReviews,
				},
				{
					numberOfReviewsToShow,
					showLoadMoreButton,
					startPageOffset: 1,
					productReviewsNameLabel: nameLabel,
					productReviewsAnonymousLabel: anonymousLabel,
				},
			);
		}
	};

	const getAndSetPrice = async (toolTipContainerEl: HTMLElement, pvc: string, options: string[], quantity: number) => {
		const priceResult = await getPrice(pvc, quantity, 0, options, getCookie("promo"), -1, "m-header__price--sup");
		const priceEls = expressQuerySelectorAll(toolTipContainerEl, ".technical-price-tag");
		const templateEl = expressQuerySelectorAll<HTMLElement>(toolTipContainerEl, ".technical-price-template")[0];
		const containerEl = expressQuerySelector<HTMLElement>(toolTipContainerEl, ".technical-price-container", true);

		containerEl.innerHTML = "";
		priceResult.Price && priceEls.forEach((el) => (el.innerHTML = priceResult.Price.FormattedPrice));
		priceResult.PriceItems &&
			priceResult.PriceItems.forEach((item) => {
				// no item properties to set => skip
				if (!item.Description && !item.CalculationDescription && !item.ItemTotal) return;

				const newEl = templateEl.cloneNode(true) as HTMLElement;
				const description = expressQuerySelector<HTMLElement>(newEl, ".technical-price-tooltip-description", true);
				item.Description ? (description.innerHTML = item.Description) : description.remove();

				const calculation = expressQuerySelector<HTMLElement>(newEl, ".technical-price-tooltip-calculation", true);
				item.CalculationDescription ? (calculation.innerHTML = item.CalculationDescription) : calculation.remove();

				const result = expressQuerySelector<HTMLElement>(newEl, ".technical-price-tooltip-result", true);
				item.ItemTotal ? (result.innerHTML = item.ItemTotal) : result.remove();

				containerEl.insertAdjacentElement("beforeend", newEl);
			});
	};

	const initGeneratedContentSlider = (ucgSlider: HTMLElement) => {
		// convert so slider elements
		const sliderElements: ICustomPhotoSliderItem[] = [];
		for (let z = 0, l = ucgSlider.children.length; z < l; z++)
			sliderElements.push({ template: ucgSlider.children[z] as HTMLElement });

		// settings for slider
		const settings: ICustomPhotoSliderWithDivSettings = {
			sliderElements: sliderElements,
			sliderContainerSettings: {
				extraSliderClass: "m-product-slider__slider-container",
				elementsContainerSettings: {
					extraSliderClass: "m-product-slider__slider-content",
					slideClicked: (e: Event, settings: ICustomPhotoSliderWithDivSettings) => {
						const selectedSlide = ((e.target as HTMLElement).closest("img") ||
							(e.target as HTMLElement).closest("picture") ||
							(e.target as HTMLElement).closest("video")) as HTMLImageElement;
						if (!selectedSlide) return;

						const selectedSlideSrc =
							selectedSlide instanceof HTMLVideoElement
								? expressQuerySelector(selectedSlide, "source")?.src
								: selectedSlide.src;
						const sliderElementIndex = settings.sliderElements.findIndex((x) => {
							if (!x.template) return;
							const sliderItemTemplate =
								expressQuerySelector<HTMLImageElement>(x.template, "img") ||
								expressQuerySelector<HTMLIFrameElement>(x.template, "picture") ||
								expressQuerySelector<HTMLSourceElement>(x.template, "video > source");
							return (
								sliderItemTemplate && (sliderItemTemplate.dataset.src || sliderItemTemplate.src) === selectedSlideSrc
							);
						});

						const slider = getParentWithClassName<HTMLElement>(selectedSlide, "technical-ugc-slider");
						let tooltipComponent: IToolTip;
						createPopup({
							dialogId: `UGC_${sliderElementIndex}`,
							view: "UserGeneratedContent",
							disposeOnClose: true,
							data: {
								userGeneratedContentContentKey: slider && slider.dataset.ugccontentkey,
								productCategoryContentKey: slider && slider.dataset.productcategorycontentkey,
								selectedIndex: sliderElementIndex,
								showPriceCurrency: window.context.removeCurrencySymbolFormUpperFunnelPages === "false",
							},
							onOpen: (popupEl: HTMLElement) => {
								tooltipComponent && tooltipComponent.dispose();

								const priceContainer = expressQuerySelector<HTMLButtonElement>(
									popupEl,
									".technical-header-price-container",
									false,
								);
								if (priceContainer) {
									const button = expressQuerySelector<HTMLButtonElement>(popupEl, ".technical-continue-button", true);
									const tooltip = expressQuerySelector<HTMLElement>(popupEl, ".technical-price-tooltip", true);
									const pvc = getParameter("pvc", button.getAttribute("href"));
									const options = getParameter("options", button.getAttribute("href"))?.split(",");
									const quantity = parseInt(button.dataset.quantity, 10) || 1;

									tooltipComponent = initTooltip({
										openClass: "is-tooltip-open",
										containerEl: tooltip,
										buttonEl: expressQuerySelector<HTMLElement>(priceContainer, ".technical-price-info-icon", true),
										onOpen: () => getAndSetPrice(priceContainer, pvc, options, quantity),
									});
								}

								const buttons = popupEl.querySelectorAll("button[href]");
								buttons.forEach((el) => {
									el.addEventListener("click", (ev) => {
										ev.preventDefault();

										const externalLink = el.getAttribute("data-external");
										const link = el.getAttribute("href");
										if (externalLink) {
											window.open(link, "_blank").focus();
										} else {
											window.location.href = link;
										}
									});
								});
							},
							onClose: () => {
								tooltipComponent && tooltipComponent.dispose();
							},
						}).then((res) => res.openAsync());
					},
				},
			},
			translateForMovement: false,
		};

		// create slider
		if (ucgSlider.parentElement) createCustomSlider(ucgSlider.parentElement, settings);
	};

	init();
});
